import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function TrialExpiredDialog({ open, onClose, trialTier }) {
  const navigate = useNavigate();
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Track if we've shown this dialog in this session
    if (open && !hasShown) {
      setHasShown(true);
      sessionStorage.setItem('gwc_trial_expired_shown', 'true');
    }
  }, [open, hasShown]);

  const handleUpgrade = () => {
    onClose();
    navigate('/account');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #fff 0%, #f5f5f5 100%)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pt: 4,
        pb: 2,
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: '#fff'
      }}>
        <Box sx={{ fontSize: '48px', mb: 1 }}>⏰</Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Your Free Trial Has Ended
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#555' }}>
          Thank you for trying Imagagrant! To continue using our AI-powered grant writing tools, please upgrade to a paid plan.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#003366', fontWeight: 'bold' }}>
            What you can still do:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon sx={{ color: '#4caf50' }} />
              </ListItemIcon>
              <ListItemText primary="View your past grant applications" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon sx={{ color: '#4caf50' }} />
              </ListItemIcon>
              <ListItemText primary="Download your existing work" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon sx={{ color: '#4caf50' }} />
              </ListItemIcon>
              <ListItemText primary="Access your account settings" />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ 
          background: '#fff3cd', 
          border: '1px solid #ffc107',
          borderRadius: '8px',
          p: 2,
          mb: 2
        }}>
          <Typography variant="h6" sx={{ mb: 1, color: '#856404', fontWeight: 'bold' }}>
            What requires an active subscription:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <BlockIcon sx={{ color: '#d32f2f' }} />
              </ListItemIcon>
              <ListItemText primary="Creating new grants" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BlockIcon sx={{ color: '#d32f2f' }} />
              </ListItemIcon>
              <ListItemText primary="Editing existing grants" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BlockIcon sx={{ color: '#d32f2f' }} />
              </ListItemIcon>
              <ListItemText primary="Using the AI chat assistant" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BlockIcon sx={{ color: '#d32f2f' }} />
              </ListItemIcon>
              <ListItemText primary="Exploring foundations database" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BlockIcon sx={{ color: '#d32f2f' }} />
              </ListItemIcon>
              <ListItemText primary="Creating/managing clients (consultants)" />
            </ListItem>
          </List>
        </Box>

        <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
          Your data is safe and will be available when you upgrade!
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ 
            mr: 2,
            color: '#666',
            borderColor: '#ccc'
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleUpgrade}
          variant="contained"
          sx={{
            backgroundColor: '#FFD700',
            color: '#003366',
            fontWeight: 'bold',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            '&:hover': {
              backgroundColor: '#e6c200',
              boxShadow: '0 0 15px rgba(255,215,0,0.4)'
            }
          }}
        >
          View Plans & Upgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
}
